import { Cog6ToothIcon } from "@heroicons/react/24/outline";
import {
  CaretSortIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  SlashIcon,
} from "@radix-ui/react-icons";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import clsx from "clsx";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CompanyData, useGetCompany } from "src/api/Companies/companies";
import { useGetCustomFields } from "src/api/CustomFields/custom-fields";
import { useGetCompanyNotes } from "src/api/Notes/notes";
import {
  OnboardingProjectData,
  ProjectsSort,
  ProjectsV2FilterGroup,
  useGetOnboardingProjects,
  useGetProjectsDetails,
  useGetProjectsV2,
} from "src/api/Services/Projects/projects";
import { PhaseStatus } from "src/api/Services/SharedSpace/phases";
import { useGetJourneyTemplates } from "src/api/Services/Tasks/journeys";
import { useGetTaskersForInstance } from "src/api/Services/Tasks/tasks";
import { useGetSpaces } from "src/api/Spaces/spaces";
import { Button } from "src/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "src/components/ui/command";
import { Loading } from "src/components/ui/loading";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "src/components/ui/tabs";
import { TextInput } from "src/components/ui/text-input";
import { getTimeDifference } from "src/utils/functions/dateTime";
import { fuzzyFilter } from "../Services/Projects/ProjectsTable";
import { SortToggle } from "../Services/Projects/columns";
import { getPhaseStatus } from "../Services/SharedSpace/Phases/Tabs/Overview";
import {
  addSearchToFilterGroup,
  clean,
  ColumnsToDisplay,
  GeneralProjectsList,
  SearchInput,
} from "../spaces/space/projects";
import CompanyNotes from "./company-notes";
// import ProjectsTable from "../Services/Projects/ProjectsTable";

type Props = {};

export default function Company({}: Props) {
  const { companyId, tab } = useParams();

  const navigate = useNavigate();
  const { data: company } = useGetCompany(String(companyId));
  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="flex h-12 shrink-0 items-center justify-between border-b border-gray-200/70 px-8">
        <div className="flex items-center space-x-2 text-sm">
          <Link
            className="text-gray-800 transition-all hover:text-gray-800"
            to="/companies"
          >
            Companies
          </Link>
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          <div className="flex cursor-default items-center space-x-1.5 text-gray-800">
            {company?.source === "hubspot" && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="3 3.5 12 12"
                className="w-4"
              >
                {" "}
                <path
                  fill="#FF5C35"
                  d="M11.54 7.422V6.096a1.02 1.02 0 0 0 .588-.92v-.03c0-.56-.46-1.02-1.02-1.02h-.03c-.561 0-1.02.46-1.02 1.02v.03c0 .407.243.757.589.92v1.326c-.516.08-.986.292-1.375.605l-3.64-2.832a1.14 1.14 0 0 0 .04-.286 1.15 1.15 0 1 0-1.15 1.148c.206 0 .398-.059.566-.154l3.58 2.786a2.892 2.892 0 0 0-.482 1.603c0 .62.196 1.194.527 1.666l-1.089 1.09a.935.935 0 0 0-.27-.045.945.945 0 1 0 .945.945.934.934 0 0 0-.044-.27l1.077-1.077a2.89 2.89 0 0 0 1.761.598 2.905 2.905 0 0 0 .446-5.776Zm-.447 4.36a1.49 1.49 0 1 1 0-2.98 1.49 1.49 0 0 1 0 2.98Z"
                />
              </svg>
            )}
            <div>{company?.name}</div>
          </div>
        </div>
        <Button
          variant={"ghost"}
          onClick={() => {
            navigate(`/companies/${companyId}/settings`);
          }}
          size="icon"
        >
          <Cog6ToothIcon className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex flex-1">
        <div className="flex h-full flex-1 flex-col overflow-auto">
          <Tabs
            defaultValue="projects"
            value={tab}
            className="flex flex-1 flex-col overflow-auto pt-3"
            onValueChange={(value) => {
              navigate(`/companies/${companyId}/${value}`);
            }}
          >
            <TabsList className="shrink-0 border-gray-200/[.65] px-8 ">
              {/* <TabsTrigger
                className="font-normal text-gray-500/80"
                value="overview"
              >
                Overview
              </TabsTrigger> */}
              <TabsTrigger className="font-normal" value="projects">
                Projects
              </TabsTrigger>
              <TabsTrigger className="font-normal" value="notes">
                Notes
              </TabsTrigger>
              {/* <TabsTrigger
                className="font-normal text-gray-500/80"
                value="tasks"
              >
                Tasks
              </TabsTrigger>
              <TabsTrigger
                className="font-normal text-gray-500/80"
                value="emails"
              >
                Emails
              </TabsTrigger>
              <TabsTrigger
                className="font-normal text-gray-500/80"
                value="notes"
              >
                Notes
              </TabsTrigger>
              <TabsTrigger
                className="font-normal text-gray-500/80"
                value="activity"
              >
                Activity
              </TabsTrigger> */}
            </TabsList>
            <div className="flex-1 overflow-auto">
              {/* <TabsContent value="overview" className="h-full">
                {!!company && <Overview company={company} />}
              </TabsContent> */}
              <TabsContent value="projects" className="h-full">
                <ProjectsV2 />
              </TabsContent>
              <TabsContent value="notes" className="h-full">
                <CompanyNotes />
              </TabsContent>
              {/* <TabsContent value="tasks" className="h-full">
                <div>Tasks</div>
              </TabsContent>
              <TabsContent value="emails" className="h-full">
                <div>Emails</div>
              </TabsContent>
              <TabsContent value="activity" className="h-full">
                <div>Activity</div>
              </TabsContent> */}
            </div>
          </Tabs>
          <div></div>
        </div>
      </div>

      {/* <div className="flex flex-1 overflow-auto">
        
      </div> */}
    </div>
  );
}

function Notes() {
  const { companyId } = useParams();
  const { data: notes } = useGetCompanyNotes(String(companyId));
  return <div>{notes?.length}</div>;
}

function Overview({ company }: { company: CompanyData }) {
  return (
    <div className="flex h-full">
      <div className="flex-1 px-8 pt-6">Overview</div>
      <div className="h-full w-[480px] space-y-6 overflow-auto border-l border-l-gray-200/70 px-8 pt-6">
        <div className="space-y-2.5">
          <div className="cursor-default text-base font-medium leading-none text-gray-600">
            Overview
          </div>
          <div className="space-y-1">
            <div className="flex min-h-[32px] items-center gap-6">
              <div className="w-32 cursor-default text-[13px] text-gray-400">
                Owner
              </div>
              {!!company && <CompanyOwnerCombobox company={company} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CompanyOwnerCombobox({ company }: { company: CompanyData }) {
  const [open, setOpen] = useState(false);
  const { data: instanceMembers } = useGetTaskersForInstance();
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={clsx(
            "group -ml-1.5 flex min-h-[32px] w-full items-center justify-between space-x-2 rounded-md px-1.5 py-1 hover:bg-gray-100",
            open && "bg-gray-100"
          )}
        >
          {/* {instanceMembers?.find((m) => m._id === phase.ownerUser._id)?.name}  */}
          TODO: Company Owner
          <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-0 group-hover:opacity-40" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="pointer-events-auto w-fit min-w-[200px] max-w-[700px] p-0"
        align="start"
      >
        <Command>
          <CommandInput placeholder="Search.." className="h-9" />
          <CommandList>
            <CommandEmpty>No options found.</CommandEmpty>
            <CommandGroup>
              {instanceMembers?.map((option) => (
                <CommandItem
                  key={option._id}
                  value={option.name}
                  onSelect={() => {
                    // TODO - update company owner
                    setOpen(false);
                  }}
                  className="justify-between gap-2"
                >
                  <span className="truncate">{option.name}</span>
                  {/* {option._id === company.ownerUser._id && (
                  <CheckIcon className="h-4 w-4" />
                )} */}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function ProjectsV2() {
  const { data: customFields } = useGetCustomFields("project");
  const [columnsToDisplay, setColumnsToDisplay] = useState<ColumnsToDisplay>(
    () => {
      return x();
    }
  );

  function x(): ColumnsToDisplay {
    let initialColumnsToDisplay: ColumnsToDisplay = {
      // company: {
      //   id: "company",
      //   visible: true,
      //   name: "Company",
      //   width: 208,
      //   isCustomField: false,
      // },
      internalUpdates: {
        id: "internalUpdates",
        visible: true,
        name: "Updates",
        width: 208,
        isCustomField: false,
      },
      owner: {
        id: "owner",
        visible: true,
        name: "Owner",
        width: 208,
        isCustomField: false,
      },
      deltaToTarget: {
        id: "deltaToTarget",
        visible: true,
        name: "Target vs Forecast",
        width: 208,
        isCustomField: false,
      },
      daysActive: {
        id: "daysActive",
        visible: true,
        name: "Days Active",
        width: 140,
        isCustomField: false,
      },
      startDate: {
        id: "startDate",
        visible: true,
        name: "Start Date",
        width: 208,
        isCustomField: false,
      },
      endDate: {
        id: "endDate",
        visible: true,
        name: "End Date",
        width: 208,
        isCustomField: false,
      },
      forecastedCompletionDate: {
        id: "forecastedCompletionDate",
        visible: true,
        name: "Forecasted End Date",
        width: 208,
        isCustomField: false,
      },
      template: {
        id: "template",
        visible: true,
        name: "Template",
        width: 208,
        isCustomField: false,
      },
    };

    // add custom fields
    if (customFields) {
      for (const customField of customFields) {
        initialColumnsToDisplay[customField._id] = {
          id: customField._id,
          visible: false,
          name: customField.name,
          width: 160,
          isCustomField: true,
        };
      }
    }

    // const savedColumnsToDisplay = localStorage.getItem(
    //   getCompanyProjectsColumnsToDisplayKey(String(companyId))
    // );
    // if (savedColumnsToDisplay) {
    //   const parsedColumnsToDisplay = JSON.parse(savedColumnsToDisplay);
    //   // set visbility based on saved state
    //   for (const key of Object.keys(initialColumnsToDisplay)) {
    //     if (parsedColumnsToDisplay.hasOwnProperty(key)) {
    //       // console.log(savedColumnsToDisplay[column.id])
    //       initialColumnsToDisplay[key] = {
    //         ...initialColumnsToDisplay[key],
    //         visible: !!parsedColumnsToDisplay[key].visible,
    //       };
    //     }
    //   }
    // }
    // console.log(initialColumnsToDisplay);
    return initialColumnsToDisplay;
  }

  useEffect(() => {
    if (customFields) {
      setColumnsToDisplay(x());
    }
  }, [customFields]);
  const [search, setSearch] = useState("");
  const [totalNoOfPages, setTotalNoOfPages] = useState<number | undefined>(
    undefined
  );

  const [sort, setSort] = useState<ProjectsSort>({
    property: "createdAt",
    order: "desc",
  });

  const [pageNo, setPageNo] = useState(1);

  useEffect(() => {
    setPageNo(1);
  }, [search]);

  const [filterGroup, setFilterGroup] = useState<ProjectsV2FilterGroup>(() => {
    // const savedFilterGroup = localStorage.getItem(
    //   getSpaceProjectsFilterKey(String(spaceId))
    // );
    // if (savedFilterGroup) {
    //   return JSON.parse(savedFilterGroup);
    // }
    return {
      logic: "AND",
      children: [
        // {
        //   field: "project-status",
        //   comparator: "in",
        //   value: ["In Progress"],
        // },
      ],
    };
  });
  const { companyId } = useParams();

  const { data: projects } = useGetProjectsV2(
    {
      logic: "AND",
      children: [
        {
          field: "company",
          comparator: "in",
          value: [String(companyId)],
        },
      ],
    },
    addSearchToFilterGroup(clean(filterGroup), search),
    pageNo,
    sort
  );

  useEffect(() => {
    if (projects?.pagination.totalPages === 0) {
      setTotalNoOfPages(1);
    } else if (projects?.pagination.totalPages) {
      setTotalNoOfPages(projects?.pagination.totalPages);
    }
  }, [projects?.pagination.totalPages]);

  const { data: projectDetails } = useGetProjectsDetails(
    {
      logic: "AND",
      children: [
        {
          field: "company",
          comparator: "in",
          value: [String(companyId)],
        },
      ],
    },
    addSearchToFilterGroup(clean(filterGroup), search),
    pageNo,
    sort
  );

  const [noOfProjectsAfterSearch, setNoOfProjectsAfterSearch] = useState(0);
  const { journeyTemplates } = useGetJourneyTemplates();

  // useEffect(() => {
  //   if (filterGroup.children.length === 0) {
  //     localStorage.removeItem(getSpaceProjectsFilterKey(String(spaceId)));
  //   } else {
  //     localStorage.setItem(
  //       getSpaceProjectsFilterKey(String(spaceId)),
  //       JSON.stringify(filterGroup)
  //     );
  //   }
  // }, [filterGroup]);

  // useEffect(() => {
  //   const savedColumnsToDisplay = localStorage.getItem(
  //     getSpaceProjectsColumnsToDisplayKey(String(spaceId))
  //   );

  //   if (savedColumnsToDisplay) {
  //     let parsedColumnsToDisplay = JSON.parse(savedColumnsToDisplay);
  //     // set visbility of localstorage based on columnsToDisplay
  //     for (const key of Object.keys(parsedColumnsToDisplay)) {
  //       if (columnsToDisplay.hasOwnProperty(key)) {
  //         parsedColumnsToDisplay[key].visible = columnsToDisplay[key].visible;
  //       }
  //     }
  //     // add new columns to localstorage
  //     for (const key of Object.keys(columnsToDisplay)) {
  //       if (!parsedColumnsToDisplay.hasOwnProperty(key)) {
  //         parsedColumnsToDisplay[key] = columnsToDisplay[key];
  //       }
  //     }
  //     localStorage.setItem(
  //       getSpaceProjectsColumnsToDisplayKey(String(spaceId)),
  //       JSON.stringify(parsedColumnsToDisplay)
  //     );
  //   } else {
  //     localStorage.setItem(
  //       getSpaceProjectsColumnsToDisplayKey(String(spaceId)),
  //       JSON.stringify(columnsToDisplay)
  //     );
  //   }
  // }, [columnsToDisplay]);
  const { data: company } = useGetCompany(String(companyId));
  return (
    <div className="flex h-full flex-col overflow-y-auto">
      {/* <div className="flex h-12 items-center border-b border-gray-200/70 px-8">
        <div className="flex items-center space-x-2 text-sm">
          <div className="flex cursor-default items-center space-x-1.5 text-gray-800">
            {space && <SpaceSymbol color={space?.color} />}
            <span>{space?.name}</span>
          </div>
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          <div className="cursor-default text-gray-800">{"Projects"}</div>
        </div>
      </div> */}

      <div className="flex h-16 items-center justify-between border-b border-gray-200/70 px-8">
        <div className="flex items-center space-x-2">
          {/* <Filter
            filterGroup={filterGroup}
            setFilterGroup={setFilterGroup}
            isLoading={!projectOwners}
            // localStorageKey="project-filter"
            filterModels={[
              {
                field: "project-owner",
                projectOwners:
                  // Array.from(
                  //   new Map(
                  //     projects?.projects.map((p) => [
                  //       p.owner._id,
                  //       {
                  //         _id: p.owner._id,
                  //         name: p.owner.name,
                  //         email: p.owner.email,
                  //       },
                  //     ])
                  //   ).values()
                  // ) ?? [],
                  projectOwners?.map((m) => {
                    return {
                      _id: m._id,
                      name: m.name,
                      email: m.email,
                    };
                  }) ?? [],
              },
              { field: "project-status" },
              { field: "company" },
              {
                field: "project-template",
                projectTemplates:
                  journeyTemplates
                    ?.filter((jt) => jt.spaceId?.toString() === spaceId)
                    .map((jt) => {
                      return {
                        _id: jt._id?.toString() ?? "",
                        name: jt.name,
                      };
                    }) ?? [],
              },
            ]}
          /> */}
          {/* <StatusFilter
            filterGroup={filterGroup}
            setFilterGroup={setFilterGroup}
          />
          <Display
            columnsToDisplay={columnsToDisplay}
            setColumnsToDisplay={setColumnsToDisplay}
          /> */}
          <div className="relative">
            <SearchInput
              onChange={(value) => {
                setSearch(value);
              }}
              initialValue={search}
            />
            <div className="absolute inset-y-0 left-0 flex items-center pl-2.5">
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
            </div>
          </div>
          <div
            className={clsx(
              "flex h-8 items-center space-x-2 overflow-hidden rounded-md border border-input shadow-sm"
              // isLoading && "opacity-50"
            )}
          >
            <div
              className={clsx(
                "flex h-full w-7 items-center justify-center border-r border-input transition-all",
                pageNo === 1
                  ? "cursor-not-allowed text-gray-300"
                  : "cursor-pointer text-gray-500 hover:bg-gray-50 hover:text-gray-700"
              )}
              onClick={() => {
                if (pageNo === 1) return;
                setPageNo((prev) => prev - 1);
              }}
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </div>
            <div className="flex select-none items-center space-x-1 px-1 text-sm text-gray-600">
              <div className="min-w-[12px] text-center">{pageNo}</div>
              <SlashIcon className="h-3 w-3 text-gray-500" />
              <div className="min-w-[12px] text-center">
                {totalNoOfPages ? (
                  totalNoOfPages
                ) : (
                  <div className="h-3 w-3 animate-pulse rounded bg-gray-200" />
                )}
              </div>
            </div>
            <div
              className={clsx(
                "flex h-full w-7 items-center justify-center border-l border-input transition-all",
                pageNo === totalNoOfPages
                  ? "cursor-not-allowed text-gray-300"
                  : "cursor-pointer text-gray-500 hover:bg-gray-50 hover:text-gray-700"
              )}
              onClick={() => {
                if (pageNo === totalNoOfPages) return;
                setPageNo((prev) => prev + 1);
              }}
            >
              <ChevronRightIcon className="h-4 w-4" />
            </div>
          </div>
          {/* {!!projects && (
            <div className="text-sm text-gray-600 h-8 rounded-md px-2 flex items-center">
              {`Showing ${noOfProjectsAfterSearch} of ${projects.noOfProjects} projects`}
            </div>
          )} */}
          {/* <Button variant={"secondary"}>Display</Button> */}
        </div>
        {/* {spaces && <NewProjectButton spaces={spaces} />} */}
      </div>

      <div className="flex flex-1 flex-col overflow-y-auto">
        {!!projects ? (
          <>
            {projects.pagination.baselineCount > 0 ? (
              <>
                <GeneralProjectsList
                  projects={projects.projects}
                  projectDetails={projectDetails?.projects}
                  columnsToDisplay={columnsToDisplay}
                  setColumnsToDisplay={setColumnsToDisplay}
                  // filterGroup={filterGroup}
                  // search={search}
                  // setSearch={setSearch}
                  sort={sort}
                  setSort={setSort}
                  // setNoOfProjectsAfterSearch={setNoOfProjectsAfterSearch}
                />
              </>
            ) : (
              <div className="mx-auto mt-[10vh]">
                <div className="flex">
                  <div className="h-20 w-20"></div>
                  <div className="h-20 w-[420px] bg-gradient-to-t from-gray-200 to-transparent px-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                  <div className="h-20 w-20"></div>
                </div>
                <div className="flex">
                  <div className="w-20 bg-gradient-to-l from-gray-200 to-white py-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                  <div className="w-[420px] border bg-gray-50/70 p-12">
                    <div className="text-xl font-medium">Projects</div>
                    <div className="pt-2.5 font-light text-gray-700">
                      You currently have no projects with {company?.name}.
                      Create a project with them in one of your spaces to get
                      started.
                    </div>
                  </div>
                  <div className="w-20 bg-gradient-to-r from-gray-200 to-white py-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                </div>
                <div className="flex">
                  <div className="h-20 w-20"></div>
                  <div className="h-20 w-[420px] bg-gradient-to-b from-gray-200 to-white px-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                  <div className="h-20 w-20"></div>
                </div>
              </div>
            )}
          </>
        ) : (
          <Loading className="mt-[20vh]" />
        )}
      </div>
    </div>
  );
}

function Projects() {
  const [search, setSearch] = useState("");
  const { companyId } = useParams();
  const { data: company } = useGetCompany(String(companyId));
  const filter = {
    status: ["Canceled", "In Progress", "Completed", "Paused"] as PhaseStatus[],
    customerIds: [String(companyId)],
  };
  const { data: projects } = useGetOnboardingProjects(filter);
  const { data: spaces } = useGetSpaces();
  return (
    <>
      <div className="flex h-16 items-center justify-between space-x-2 border-b border-gray-200/70 px-8">
        <div className="relative">
          <TextInput
            className="h-8 w-72 pl-8"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            placeholder="Search.."
            autoFocus
          />
          <div className="absolute inset-y-0 left-0 flex items-center pl-2.5">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
          </div>
        </div>
        {/* <Dialog>
            <DialogTrigger asChild>
              <Button variant={"secondary"}>New Project</Button>
            </DialogTrigger>
            <DialogContent className="max-h-[60vh] max-w-3xl p-0">

            </DialogContent>
        </Dialog> */}
        {/* {!!spaces && !!company && (
          <NewProjectButton spaceId={spaces ?? []} step={0} customer={company} />
        )} */}
      </div>
      {!!projects && !!company ? (
        <>
          {projects.length > 0 ? (
            <ProjectsTable
              projects={projects}
              search={search}
              setSearch={setSearch}
            />
          ) : (
            <>
              <div className="mx-auto mt-[10vh] w-fit">
                <div className="flex">
                  <div className="h-20 w-20"></div>
                  <div className="h-20 w-[420px] bg-gradient-to-t from-gray-200 to-transparent px-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                  <div className="h-20 w-20"></div>
                </div>
                <div className="flex">
                  <div className="w-20 bg-gradient-to-l from-gray-200 to-white py-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                  <div className="w-[420px] border bg-gray-50/70 p-12">
                    <div className="text-xl font-medium">Projects</div>
                    <div className="pt-2.5 font-light text-gray-700">
                      {/* <div>
                        Projects with customers help you give them visibility
                        into projects where they are actively involved.
                      </div> */}
                      <div>
                        You currently have no projects with {company?.name}.
                        Create a project with them in one of your spaces to get
                        started.
                      </div>
                    </div>
                    {/* <div className="pt-6">
                      <NewProjectButton spaces={spaces} />
                    </div> */}
                  </div>
                  <div className="w-20 bg-gradient-to-r from-gray-200 to-white py-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                </div>
                <div className="flex">
                  <div className="h-20 w-20"></div>
                  <div className="h-20 w-[420px] bg-gradient-to-b from-gray-200 to-white px-[1px]">
                    <div className="h-full w-full bg-white" />
                  </div>
                  <div className="h-20 w-20"></div>
                </div>
              </div>
            </>
          )}
        </>
      ) : (
        <Loading className="mt-[20vh]" />
      )}
    </>
  );
}

function ProjectsTable({
  projects,
  search,
  setSearch,
}: {
  projects: OnboardingProjectData[];
  search: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
}) {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([]);
  console.log(projects);
  const table = useReactTable({
    data: projects ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      globalFilter: search,
    },
    globalFilterFn: fuzzyFilter,
    onSortingChange: setSorting,
    onGlobalFilterChange: setSearch,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <table
      style={{ borderCollapse: "separate", borderSpacing: 0 }}
      className="w-full"
    >
      <thead className="overflow-auto">
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id} className="border-b">
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                scope="col"
                className={
                  "sticky top-0 z-10 h-11 whitespace-nowrap border-b border-r border-gray-200/70 bg-gray-50 px-6 text-left text-xs font-normal uppercase tracking-wide text-gray-400 first:left-0 first:z-20 first:pl-8 last:w-full last:border-r-0"
                }
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody className="divide-y divide-gray-200 bg-white">
        {!projects ? (
          <tr>
            {/* <td className="inset-0 flex items-center justify-center p-4 pl-8"> */}
            <div className="sticky left-0 z-10 p-4 pl-8 text-gray-400">
              Loading...
            </div>
            {/* </td> */}
          </tr>
        ) : (
          <>
            {projects.length === 0 ? (
              <tr>
                <div className="sticky left-0 z-10 whitespace-nowrap p-4 pl-8 text-gray-400">
                  No projects
                </div>
                {/* <td className="inset-0 flex items-center justify-center p-4">
                    </td> */}
              </tr>
            ) : (
              <>
                {table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className="group relative rounded-lg hover:cursor-pointer hover:bg-gray-50"
                    onClick={() => {
                      navigate(`/projects/${row.original._id}/tasks`);
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className={
                          "whitespace-nowrap border-r border-b border-gray-100 px-6 py-3 text-sm text-gray-500 first:sticky first:left-0 first:z-10 first:bg-white first:pr-6 first:font-medium first:text-gray-900 last:border-r-0 first:group-hover:bg-gray-50 first:sm:pl-8"
                        }
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </>
            )}
          </>
        )}
      </tbody>
    </table>
  );
}

const columns: ColumnDef<OnboardingProjectData>[] = [
  {
    id: "name",
    header: "Name",
    cell: ({ row }) => row.original.name,
    accessorFn: ({ name }) => name,
  },
  // {
  //   id: "space",
  //   header: "Space",
  //   cell: ({ row }) => row.original.space.name,
  //   accessorFn: ({ space }) => space.name,
  // },
  {
    id: "status",
    header: ({ column }) => {
      return (
        <div className="flex items-center space-x-2">
          <span>Status</span>
        </div>
      );
    },
    cell: ({ row }) => {
      const { status } = row.original;
      return (
        <div className="h-full">
          <span>{getPhaseStatus(status)}</span>
        </div>
      );
    },
  },
  {
    id: "onboardingProgress",
    header: ({ column }) => (
      <div className="flex items-center space-x-1">
        <span>Progress</span>
        <SortToggle column={column} />
      </div>
    ),
    accessorFn: ({ journey }) => {
      // const totalTasks =
      //   allTasksBreakdown?.external?.toDo.total +
      //   allTasksBreakdown?.external?.inProgress.total +
      //   allTasksBreakdown?.external?.canceled +
      //   allTasksBreakdown?.external?.completed.late +
      //   allTasksBreakdown?.external?.completed.onTime +
      //   allTasksBreakdown?.internal?.toDo.total +
      //   allTasksBreakdown?.internal?.inProgress.total +
      //   allTasksBreakdown?.internal?.canceled +
      //   allTasksBreakdown?.internal?.completed.late +
      //   allTasksBreakdown?.internal?.completed.onTime;
      // const completedTasks =
      //   allTasksBreakdown?.external?.completed.late +
      //   allTasksBreakdown?.external?.completed.onTime +
      //   allTasksBreakdown?.internal?.completed.late +
      //   allTasksBreakdown?.internal?.completed.onTime;
      const progress = Math.round(
        (journey.completedTasks / journey.totalTasks) * 100
      ).toFixed(0);
      return progress;
    },
    sortingFn: "alphanumeric",
    cell: ({ row }) => {
      const progress = Math.round(
        (row.original.journey.completedTasks /
          row.original.journey.totalTasks) *
          100
      );
      const hasTasks = row.original.journey.totalTasks > 0;
      return (
        <>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <div
                className={clsx(
                  "h-1.5 w-20 shrink-0 overflow-hidden rounded-sm",
                  row.original.journey.completedTasks === 0
                    ? "border-gray-300 bg-gray-200/70"
                    : "border-green-200 bg-green-200/70"
                )}
              >
                <div
                  style={{ width: hasTasks ? progress + "%" : "0%" }}
                  className="h-full rounded-sm bg-green-500"
                ></div>
              </div>
              <div className="w-6 text-sm font-medium text-gray-600">
                {(hasTasks ? progress.toFixed(0) : "0") + "%"}
              </div>
            </div>
            <div className="flex h-5 w-fit items-center justify-center rounded-md bg-gray-50 px-1.5">
              <div className="text-[11px] text-gray-400">{`${row.original.journey.completedTasks}/${row.original.journey.totalTasks}`}</div>
            </div>
          </div>
        </>
      );
    },
  },
  {
    id: "forecastedCompletionDate",
    header: ({ column }) => (
      <div className="flex items-center space-x-1">
        <div>
          <span>Status</span>{" "}
          {/* <span className="text-xs lowercase text-gray-400"> vs </span>
          <span>Actual</span> */}
        </div>
        <SortToggle column={column} />
      </div>
    ),
    cell: ({ row }) => {
      if (row.original.type === "Simple") {
        return <div className="text-gray-500">-</div>;
      }
      const forecastedCompletionDate = new Date(
        row.original.keyDates.forecastedCompletionDate
      );
      const plannedCompletionDate = new Date(
        row.original.keyDates.plannedCompletionDate
      );
      let completionDate;
      let completionDelta;
      if (row.original.keyDates.completionDate) {
        completionDate = new Date(row.original.keyDates.completionDate);
        completionDelta = getTimeDifference(
          completionDate,
          plannedCompletionDate
        );
      }

      const delta = getTimeDifference(
        forecastedCompletionDate,
        plannedCompletionDate
      );
      return (
        <>
          {!!completionDate && !!completionDelta ? (
            <div>
              <div
                className={clsx(
                  "text-sm",
                  delta.isDate2After ? "text-green-500" : "text-red-500"
                )}
              >{`Completed ${delta.absValue} ${delta.unit} ${
                delta.isDate2After ? "ahead of time" : "behind schedule"
              }`}</div>
            </div>
          ) : (
            <div className="space-y-1">
              <div
                className={clsx(
                  "text-sm",
                  delta.isDate2After ? "text-green-500" : "text-red-500"
                )}
              >{`${delta.absValue} ${delta.unit} ${
                delta.isDate2After ? "ahead" : "behind"
              }`}</div>
              {/* <div
                className={clsx(
                  "text-xs text-gray-500"
                  // delta.isDate2After ? "text-green-500" : "text-red-500"
                )}
              >
                <span>{dayjs(plannedCompletionDate).format("DD MMM")}</span>
                <span className="text-gray-400"> vs </span>
                <span>{dayjs(forecastedCompletionDate).format("DD MMM")}</span>
              </div> */}
            </div>
          )}
        </>
      );
    },

    accessorFn: (row) => {
      const forecastedCompletionDate1 = new Date(
        row.keyDates.forecastedCompletionDate
      );
      const plannedCompletionDate1 = new Date(
        row.keyDates.plannedCompletionDate
      );
      const delta = getTimeDifference(
        forecastedCompletionDate1,
        plannedCompletionDate1
      );
      // console.log(`${}`)
      return delta.isDate2After
        ? delta.absDifferenceInMs
        : -delta.absDifferenceInMs;
    },
    enableSorting: true,
    // sortingFn: "alphanumeric",
    sortingFn: (row1, row2, columnId) => {
      return Number(row1.getValue(columnId)) - Number(row2.getValue(columnId));
    },
  },
  {
    id: "startedDate",
    accessorFn: ({ keyDates }) => keyDates.startedDate,
    header: ({ column }) => (
      <div className="flex items-center space-x-1">
        <span>Start Date</span>
        <SortToggle column={column} />
      </div>
    ),
    cell: ({ row }) => {
      const startedDate = new Date(row.original.keyDates.startedDate);
      const timeAgo = getTimeDifference(startedDate, new Date());
      return (
        <div className="space-y-1">
          <div className="text-sm">
            {dayjs(startedDate).format("DD MMM, YYYY")}
          </div>
          {/* <div className="text-xs text-gray-400">{`${timeAgo.absValue} ${timeAgo.unit} ago`}</div> */}
        </div>
      );
    },
    // sortingFn: (rowA, rowB) => {
    //   const startedDateA = new Date(rowA.original.keyDates.startedDate);
    //   const startedDateB = new Date(rowB.original.keyDates.startedDate);
    //   return startedDateA.getTime() - startedDateB.getTime();
    // },
    sortingFn: "datetime",
    // enableSorting: true,
  },
  {
    id: "owner",
    header: () => {
      return <span>Owner</span>;
    },
    cell: ({ row }) => {
      return (
        <div className="h-full">
          <span>{row.original.ownerUser.name}</span>
        </div>
      );
    },
  },
];
